import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender, valueRender } from '@repo/toolkit/conditional-render'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Checkbox from '@repo/ui/checkbox'
import { InformationIcon, PriceTagIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getJourneyPlace } from '../../../api/journey-place/get-journey-place'
import { getJourneyPlaces } from '../../../api/journey-place/get-journey-places'
import { getOptionPriceSuggestion } from '../../../api/option-prices/get-option-price-suggestion'
import { getOptions } from '../../../api/options/get-options'
import { OptionPriceProperties } from '../../../lib/types/models/option-prices/option-price-properties'
import { Product } from '../../../lib/types/models/product/product'
import { ProductJourneyPlaceInput } from '../../../lib/types/models/product/product-journey-place-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'

type Props = BaseFormProps<ProductJourneyPlaceInput, true> & {
  product: Pick<Product, 'countryId' | 'formulaId' | 'minAge' | 'maxAge' | 'accomodationCategory'>
  className?: string
}

const formSchema: ZodType<ProductJourneyPlaceInput> = z
  .object({
    placeId: createNumberSchema('lieu de séjour', { isPositive: true }),
    weeklyPrice: createNumberSchema('frais de séjour / semaine', { nonNegative: true }),
    optionId: createNumberSchema('option', { isPositive: true }),
    optionPrice: createNumberSchema('tarif / semaine', { nonNegative: true }),
    hasAgeLimit: z.boolean().optional(),
    mandatoryUnderAge: createNumberSchema('âge', { optional: true })
  })
  .superRefine((data, ctx) => {
    if (data.hasAgeLimit && !data.mandatoryUnderAge)
      ctx.addIssue({
        message: 'Veuillez entrer un âge',
        code: z.ZodIssueCode.custom,
        path: ['mandatoryUnderAge']
      })
  })

const ProductJourneyPlaceForm = ({
  product,
  className,
  defaultValues,
  ...validationButtonProps
}: Props) => {
  const form = useForm<ProductJourneyPlaceInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { hasAgeLimit, placeId } = form.watch()

  const { data: places } = useQuery({
    queryKey: [
      'journey-places',
      'BASIC',
      product.countryId,
      { isOtoIndividual: true, accomodationCategory: product.accomodationCategory }
    ],
    queryFn: () =>
      getJourneyPlaces({
        version: 'BASIC',
        filter: {
          countryIds: [product.countryId],
          isOtoIndividual: true,
          accomodationCategory: product.accomodationCategory
        }
      })
  })

  const { data: options } = useQuery({
    queryKey: ['options'],
    queryFn: () => getOptions({ filter: { countries: [product.countryId] } })
  })

  const { data: selectedPlace } = useQuery({
    queryKey: ['journey-place', placeId],
    queryFn: () => getJourneyPlace(placeId),
    enabled: !!placeId
  })

  const suggestOptionPrice = async (optionId: number) => {
    if (form.getValues('optionPrice')) return

    try {
      const suggestion = await getOptionPriceSuggestion({
        optionId,
        ...OptionPriceProperties.getSuggestionQuery(product)
      })
      if (suggestion) form.setValue('optionPrice', suggestion.price, { shouldValidate: true })
    } catch {}
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
        className={cn('flex flex-col gap-4', className)}
      >
        <FormField
          control={form.control}
          name={'placeId'}
          render={({ field }) => (
            <FormItem className={'flex-1'}>
              <FormControl>
                <ControlledSelectInput
                  required
                  searchable
                  {...field}
                  label={'Lieu de séjour'}
                  options={places?.data.map((place) => ({ label: place.name, value: place.id }))}
                  selectedOption={conditionalRender(!!field.value, {
                    label: places?.data.find((place) => place.id === field.value)?.name ?? '',
                    value: field.value
                  })}
                />
              </FormControl>
            </FormItem>
          )}
        />

        {selectedPlace && (
          <div className={'bg-white-hover rounded-lg p-3 flex flex-row gap-2'}>
            <InformationIcon
              height={'20'}
              width={'20'}
              color={tailwindColors.orange.DEFAULT}
              className={'shrink-0'}
            />

            <div className={'flex flex-col gap-2'}>
              <Typography
                variant={'secondary-body'}
                color={tailwindColors.orange.DEFAULT}
                className={'font-semibold'}
              >
                {'Infos Lieu :'}
              </Typography>

              <div className={'flex flex-row flex-wrap items-center gap-1.5'}>
                <Typography variant={'secondary-body'} color={tailwindColors.grey.lighter}>
                  {'•'}
                </Typography>

                <Typography
                  variant={'secondary-body'}
                  color={tailwindColors.black.DEFAULT}
                  className={'font-semibold'}
                >
                  {"Lieux d'arrivée :"}
                </Typography>

                {selectedPlace.arrivalPoint.length ? (
                  selectedPlace.arrivalPoint.map((arrivalPoint) => (
                    <Badge key={arrivalPoint.id} text={arrivalPoint.title} />
                  ))
                ) : (
                  <Typography variant={'secondary-body'} color={tailwindColors.grey.lighter}>
                    {'-'}
                  </Typography>
                )}
              </div>

              <div className={'flex flex-row flex-wrap items-center gap-1.5'}>
                <Typography variant={'secondary-body'} color={tailwindColors.grey.lighter}>
                  {'•'}
                </Typography>

                <Typography
                  variant={'secondary-body'}
                  color={tailwindColors.black.DEFAULT}
                  className={'font-semibold'}
                >
                  {'Tarif Transfert :'}
                </Typography>

                <Typography variant={'secondary-body'} color={tailwindColors.grey.lighter}>
                  {valueRender(selectedPlace.price, (price) => `${price}€`)}
                </Typography>
              </div>
            </div>
          </div>
        )}

        <FormField
          control={form.control}
          name={'weeklyPrice'}
          render={({ field }) => (
            <FormItem className={'flex-1'}>
              <FormControl>
                <ControlledInput
                  required
                  {...field}
                  type={'number'}
                  label={'Frais de séjour / semaine'}
                  icon={PriceTagIcon}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <Box className={'bg-white-hover border-grey-light border rounded-lg'}>
          <div className={'flex flex-col gap-3'}>
            <div className={'flex flex-row gap-3'}>
              <FormField
                control={form.control}
                name={'optionId'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledSelectInput
                        required
                        searchable
                        dropDownPosition="above"
                        labelClassName="bg-white-hover"
                        {...field}
                        label={'Option'}
                        options={options?.data.map((option) => ({
                          label: getTranslatedName(option),
                          value: option.id
                        }))}
                        selectedOption={conditionalRender(!!field.value, {
                          label: getTranslatedName(
                            options?.data.find((option) => option.id === field.value)
                          ),
                          value: field.value
                        })}
                        onChange={async (value) => {
                          const previousValue = field.value
                          field.onChange(value)
                          if (!value || value === previousValue) return
                          await suggestOptionPrice(value)
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'optionPrice'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledInput
                        required
                        {...field}
                        labelClassName="bg-white-hover"
                        type={'number'}
                        label={'Tarif / semaine'}
                        icon={PriceTagIcon}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className={'flex flex-row items-center gap-3'}>
              <Checkbox
                checked={hasAgeLimit}
                onChange={(value) => {
                  form.setValue('hasAgeLimit', value)
                  if (!value) form.resetField('mandatoryUnderAge')
                }}
                labelClassName="font-semibold"
                label="Obligatoire en dessous d'un certain âge ?"
              />

              <FormField
                control={form.control}
                name={'mandatoryUnderAge'}
                render={({ field, fieldState }) => (
                  <FormItem className={'w-32'}>
                    <FormControl>
                      <ControlledInput
                        required={hasAgeLimit}
                        disabled={!hasAgeLimit}
                        {...field}
                        labelClassName="bg-white-hover"
                        type={'number'}
                        label={'Âge'}
                        errorMessage={fieldState.error?.message}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </Box>

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default ProductJourneyPlaceForm
